import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Target } from "lucide-react";

import { PageHero } from "@/components/site/PageHero";
import { SiteFooter } from "@/components/site/SiteFooter";
import { FaqSection, faqJsonLd } from "@/components/site/FaqSection";
import { grade11PastPaperFaqs } from "@/content/faqs";

const DBE_NSC_URL = "https://www.education.gov.za/Curriculum/NationalSeniorCertificate(NSC)Examinations.aspx";

const subjects = [
  {
    name: "Mathematics",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Build algebra fluency before tackling calculus.",
  },
  {
    name: "Mathematical Literacy",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Practise interpreting graphs and tables.",
  },
  {
    name: "Physical Sciences",
    papers: "P1 (Physics) & P2 (Chemistry)",
    tip: "Write out formulas first, then substitute values.",
  },
  {
    name: "Life Sciences",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Label every diagram clearly in your answers.",
  },
  {
    name: "Accounting",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Check that your balance sheet actually balances.",
  },
  {
    name: "Business Studies",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Link every point back to the business scenario.",
  },
  {
    name: "Economics",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Draw graphs to illustrate each concept.",
  },
  {
    name: "English Home Language",
    papers: "P1, P2 & P3, exemplar and provincial",
    tip: "Practise writing within the time limit for each section.",
  },
  {
    name: "Afrikaans First Additional Language",
    papers: "P1, P2 & P3, exemplar and provincial",
    tip: "Build vocabulary lists by topic for comprehension.",
  },
  {
    name: "History",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Practise structuring essays with a clear introduction.",
  },
  {
    name: "Geography",
    papers: "P1 & P2, exemplar and provincial",
    tip: "Memorise map symbols before practising map work.",
  },
  {
    name: "Life Orientation",
    papers: "P1, exemplar and provincial",
    tip: "Answer in full sentences and address the rubric criteria.",
  },
];

const steps = [
  {
    step: "1",
    title: "Attempt under exam conditions",
    text: "Set a timer, use only allowed resources and work in a quiet space.",
  },
  {
    step: "2",
    title: "Mark yourself honestly",
    text: "Use the memorandum or your teacher's guidelines. Note every mark lost and the concept behind it.",
  },
  {
    step: "3",
    title: "Sort mistakes by concept",
    text: "Group errors by topic so you can see your real knowledge gaps before Grade 12.",
  },
  {
    step: "4",
    title: "Revisit weak areas",
    text: "Go back to notes, videos or textbooks for each concept you missed.",
  },
  {
    step: "5",
    title: "Repeat with a new paper",
    text: "Track whether your weak areas improve each time and carry the gains into Grade 12.",
  },
];

export const Route = createFileRoute("/grade-11-past-papers")({
  head: () => ({
    meta: [
      { title: "Grade 11 Past Papers (CAPS) — Free Exam Revision | adaptED" },
      {
        name: "description",
        content:
          "Find Grade 11 past papers and CAPS-aligned exam resources. Download exemplar and provincial Maths, Science and more, then turn every paper into targeted revision with adaptED.",
      },
      {
        name: "keywords",
        content:
          "grade 11 past papers, Grade 11 Maths past papers, Grade 11 Physical Sciences past papers, CAPS past papers, grade 11 exam papers, grade 11 revision, Grade 11 study resources, South African grade 11 past papers, grade 11 exemplar papers",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Grade 11 Past Papers (CAPS) — Free Exam Revision | adaptED" },
      {
        property: "og:description",
        content:
          "Find Grade 11 past papers and CAPS-aligned exam resources. Download exemplar and provincial Maths, Science and more, then turn every paper into targeted revision with adaptED.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/grade-11-past-papers" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "Grade 11 Past Papers (CAPS) — Free Exam Revision | adaptED" },
      {
        name: "twitter:description",
        content:
          "Find Grade 11 past papers and CAPS-aligned exam resources. Download exemplar and provincial Maths, Science and more, then turn every paper into targeted revision with adaptED.",
      },
      {
        property: "og:image",
        content: "https://www.adapted.info/__l5e/assets-v1/ddf459f9-9bda-4f99-8b5a-5df16e601132/hero-3.png",
      },
      {
        property: "og:image:alt",
        content: "adaptED, South Africa's adaptive AI learning companion for Grades 10-12.",
      },
      {
        name: "twitter:image",
        content: "https://www.adapted.info/__l5e/assets-v1/ddf459f9-9bda-4f99-8b5a-5df16e601132/hero-3.png",
      },
      {
        name: "twitter:image:alt",
        content: "adaptED, South Africa's adaptive AI learning companion for Grades 10-12.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.adapted.info/grade-11-past-papers" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://www.adapted.info/grade-11-past-papers#collection",
          url: "https://www.adapted.info/grade-11-past-papers",
          name: "Grade 11 Past Papers (CAPS) — Free Exam Revision",
          description:
            "A curated hub of Grade 11 past papers and CAPS-aligned exam resources for South African learners.",
          inLanguage: "en-ZA",
          isPartOf: { "@id": "https://www.adapted.info/#website" },
          about: {
            "@type": "Thing",
            name: "Grade 11 past papers",
          },
        }),
      },
      faqJsonLd(grade11PastPaperFaqs, "https://www.adapted.info/grade-11-past-papers"),
    ],
  }),
  component: Grade11PastPapersPage,
});

function Grade11PastPapersPage() {
  return (
    <main id="top">
      <PageHero
        label="Exam preparation"
        title="Grade 11 past papers & CAPS exam resources"
        paragraph="Download Grade 11 past papers and exemplars, and see how adaptED turns every question into a personalised revision plan before you reach matric."
      />

      <section className="surface-warm relative py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-3xl leading-tight text-foreground sm:text-4xl">
              Grade 11 past papers build the foundation for matric success
            </h2>
            <p className="mt-4 text-base text-foreground/85">
              Grade 11 is your last full year of new content before matric. Working through exemplar and provincial
              papers now shows you exactly which topics to strengthen before Grade 12 begins.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {subjects.map((subject) => (
              <article
                key={subject.name}
                className="flex flex-col rounded-2xl border border-foreground/10 bg-background/80 p-6 shadow-[var(--shadow-card)] backdrop-blur-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-lg bg-primary/10 p-2 text-primary">
                    <BookOpen className="size-5" aria-hidden="true" />
                  </div>
                  <h3 className="font-display text-lg text-foreground">{subject.name}</h3>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Papers:</span> {subject.papers}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Tip:</span> {subject.tip}
                </p>
                <a
                  href={DBE_NSC_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto pt-5 text-sm font-semibold text-primary hover:underline"
                >
                  Download official DBE papers
                </a>
              </article>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-foreground/10 bg-ink p-8 text-center text-ink-foreground shadow-[var(--shadow-card)]">
            <h3 className="font-display text-2xl">IEB & SACAI past papers</h3>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-ink-foreground/80">
              IEB schools provide past papers to their learners through the IEB portal. If you are at an IEB school, ask
              your teacher or visit the IEB website. IEB, SACAI and other curriculums will be added to adaptED soon.
            </p>
            <a
              href="https://www.ieb-online.co.za/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
            >
              Visit IEB online
            </a>
          </div>
        </div>
      </section>

      <section className="surface-ink relative py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 className="font-display text-3xl leading-tight text-ink-foreground sm:text-4xl">
                How to use past papers effectively
              </h2>
              <p className="mt-4 text-base text-ink-foreground/80">
                Don't just memorise answers. Use this simple loop to turn every paper into real progress before Grade 12.
              </p>
            </div>
            <ol className="space-y-6">
              {steps.map((item) => (
                <li key={item.step} className="flex gap-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {item.step}
                  </span>
                  <div>
                    <h3 className="font-display text-lg text-ink-foreground">{item.title}</h3>
                    <p className="mt-1 text-sm text-ink-foreground/80">{item.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="surface-warm relative py-20">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="inline-flex rounded-lg bg-primary/10 p-3 text-primary">
            <Target className="size-6" aria-hidden="true" />
          </div>
          <h2 className="mt-5 font-display text-3xl leading-tight text-foreground sm:text-4xl">
            Turn past papers into personalised revision
          </h2>
          <p className="mt-4 text-base text-foreground/85">
            adaptED reads your answers like a tutor would. It spots the exact concepts behind your mistakes and builds a
            revision path just for you — so you walk into Grade 12 with confidence.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/join-the-pilot"
              search={{ type: "early-access" }}
              hash="contact"
              className="inline-flex rounded-full bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5"
            >
              Get early access
            </Link>
            <Link
              to="/grade-12-past-papers"
              className="inline-flex rounded-full bg-ink px-8 py-4 text-sm font-semibold text-ink-foreground shadow-[var(--shadow-card)] ring-1 ring-ink-foreground/10 transition-transform hover:-translate-y-0.5"
            >
              Grade 12 past papers
            </Link>
          </div>
        </div>
      </section>

      <FaqSection
        items={grade11PastPaperFaqs}
        heading="Grade 11 past paper questions, answered"
        paragraph="Everything you need to know about finding and using Grade 11 past papers in South Africa."
        tone="warm"
      />
      <SiteFooter />
    </main>
  );
}
